import type { Finding } from './api.ts'
import { ruleHint, ruleIngredients } from './activeRules.ts'
import { diffCharacters, type CharSegment } from './charDiff.ts'
import {
  activesBoard,
  checkSides,
  reasonFor,
  type ActiveRow,
  type VerificationItem,
} from './verificationItems.ts'
import { divergenceField } from './divergenceSteps.ts'

/**
 * One divergence, said for its own step on the divergence-only pass (#641):
 * what it is about, what the receita says above what the sistema says, which
 * part of each is the difference, and why it was raised.
 *
 * On the full pass the same facts are a row among twenty, with the sentence
 * in a tooltip. Here the step *is* the divergence, so nothing is left to hover
 * for and nothing is drawn that is not about it: an ingredient's divergence
 * shows that ingredient, never the rest of its pote.
 */
export interface BriefPart {
  readonly text: string
  /** This is where the two sides disagree, and is painted. */
  readonly differs: boolean
  /** On a name, the letters that differ: "Souza" against "Sousa" shows the one. */
  readonly letters?: readonly CharSegment[] | undefined
  /** Said in place of something this side does not state, a dose the receita left out. */
  readonly absent?: boolean
}

export interface BriefSide {
  readonly heading: string
  /** Empty when this side does not have it at all: a line the receita never named. */
  readonly parts: readonly BriefPart[]
}

export interface DivergenceBrief {
  /** "Dose", "Forma farmacêutica", or the rule's own title. */
  readonly field: string
  /** The receita, then the sistema. Absent for a rule, which compares nothing. */
  readonly sides?: readonly [BriefSide, BriefSide] | undefined
  /** Why it was raised, in the finding's own sentence. */
  readonly reason?: string | undefined
  /** The ativos a rule is about. */
  readonly ingredients?: readonly string[] | undefined
}

export const PRESCRIPTION_HEADING = 'Receita'
export const SYSTEM_HEADING = 'Sistema'
/** A label finding holds the receita against the printed label, not the sistema. */
const LABEL_HEADING = 'Rótulo'

/** Checks whose two sides are names, where the letters that differ are the point. */
const NAMED = new Set(['patient', 'prescriber', 'identity'])

/*
 * Past this, a value is prose -- a posology, a list -- and painting all of it
 * says nothing the side's colour does not. The sentence below says what
 * differs in it.
 */
const PAINTABLE = 60

export function divergenceBrief(finding: Finding, item?: VerificationItem): DivergenceBrief {
  const field = divergenceField(finding)
  const reason = reasonFor(finding)

  if (finding.check === 'operational') {
    const ingredients = ruleIngredients((finding.evidence ?? {}) as Record<string, unknown>)
    return {
      field,
      reason: ruleHint(finding, reason),
      ...(ingredients.length === 0 ? {} : { ingredients }),
    }
  }

  const row = item === undefined ? undefined : divergenceRow(item, finding)
  const sides = row === undefined ? valueSides(finding) : rowSides(row, finding)
  return {
    field,
    ...(sides === undefined ? {} : { sides }),
    ...(reason === undefined ? {} : { reason }),
  }
}

/**
 * The ingredient line a divergence is about, when it is about one: the row the
 * full pass would draw it on, so both passes agree on what was compared.
 */
export function divergenceRow(item: VerificationItem, finding: Finding): ActiveRow | undefined {
  return activesBoard(item).rows.find((row) =>
    row.findings.some((one) => one.fingerprint === finding.fingerprint),
  )
}

/**
 * An ingredient, the receita's line above the keyed one.
 *
 * Which part is painted follows what was found: the dose on a dose, the name
 * on an identity, the whole line where the line itself is what is missing or
 * extra. The other part is still shown, unpainted, so the reader knows which
 * ingredient this is without the rest of the pote around it.
 */
function rowSides(row: ActiveRow, finding: Finding): readonly [BriefSide, BriefSide] {
  const whole = finding.check !== 'dose' && finding.check !== 'identity'
  const doseDiffers = finding.check === 'dose' || whole
  const nameDiffers = finding.check === 'identity' || whole
  const letters =
    finding.check === 'identity' && row.name !== undefined && row.quotedName !== undefined
      ? lettersOf(row.name, row.quotedName)
      : undefined

  const side = (
    heading: string,
    name: string | undefined,
    dose: string | undefined,
    own: readonly CharSegment[] | undefined,
  ): BriefSide => {
    if (name === undefined) return { heading, parts: [] }
    const parts: BriefPart[] = [
      { text: name, differs: nameDiffers, ...(own === undefined ? {} : { letters: own }) },
    ]
    if (dose !== undefined) parts.push({ text: dose, differs: doseDiffers })
    // A dose divergence with no dose on one side: that absence is the difference.
    else if (finding.check === 'dose') parts.push({ text: 'sem dose', differs: true, absent: true })
    return { heading, parts }
  }

  return [
    side(PRESCRIPTION_HEADING, row.name, row.prescribedDose, letters?.prescription),
    side(SYSTEM_HEADING, row.quotedName, row.quotedDose, letters?.system),
  ]
}

/** Anything else: one value a side, as the full pass's board compares them. */
function valueSides(finding: Finding): readonly [BriefSide, BriefSide] | undefined {
  const { prescribed, quoted } = checkSides(finding)
  if (prescribed === undefined && quoted === undefined) return undefined
  const letters =
    NAMED.has(finding.check) && prescribed !== undefined && quoted !== undefined
      ? lettersOf(prescribed, quoted)
      : undefined
  const part = (text: string | undefined, own: readonly CharSegment[] | undefined) =>
    text === undefined
      ? []
      : [
          {
            text,
            differs: text.length <= PAINTABLE,
            ...(own === undefined ? {} : { letters: own }),
          },
        ]
  return [
    { heading: PRESCRIPTION_HEADING, parts: part(prescribed, letters?.prescription) },
    {
      heading: finding.check === 'label' ? LABEL_HEADING : SYSTEM_HEADING,
      parts: part(quoted, letters?.system),
    },
  ]
}

/**
 * The letters that differ, or nothing when none do.
 *
 * Case and accents are not differences (`charDiff.ts`), so two names flagged
 * for something else -- a reading the scorer normalised differently -- can
 * come back with no letter to paint. Painting none would say they agree; the
 * whole name is painted instead.
 */
function lettersOf(
  prescription: string,
  system: string,
): { prescription: CharSegment[]; system: CharSegment[] } | undefined {
  const cut = diffCharacters(prescription, system)
  const any = [...cut.prescription, ...cut.system].some((segment) => segment.differs)
  return any ? cut : undefined
}
